import { FormBuilder } from '@/components/formBuilder';
import { useRef, ReactNode } from 'react';
import * as Yup from 'yup';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  Button,
  ModalHeader,
  ModalFooter,
  ModalCloseButton,
} from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';

interface DeleteConfirmationModalProps {
  onSubmit?: () => void;
  onClose?: () => void;
  submitButtonLabel?: string;
  cancelButtonLabel?: string;
  submitButtonIsLoading?: boolean;
  title?: string;
  subTitle?: string;
  primaryButtonProps?: Record<string, any>;
  primaryVariant?: string;
  showDeleteInput?: boolean;
  customBody?: ReactNode;
}

export const DeleteConfirmationModal = ({
  onSubmit = () => {},
  onClose = () => {},
  submitButtonLabel = 'Submit',
  cancelButtonLabel = 'Cancel',
  submitButtonIsLoading = false,
  title = 'Create Custom Report',
  subTitle = 'Create Custom Report',
  primaryButtonProps = {},
  primaryVariant = 'primary',
  showDeleteInput = true,
  customBody,
}: DeleteConfirmationModalProps) => {
  const formRef = useRef(null);

  const formConfig = [
    {
      name: 'name',
      type: 'kInput',
      isRequired: true,
      placeholder: 'Type "DELETE" to confirm',
      errorMessage: 'Confirmation message is required',
      isInputSubmit: true,
      validation: Yup.string()
        .oneOf(['DELETE'], `Confirmation message doesn't match`)
        .required('Confirmation message is required'),
    },
  ];

  const formSubmit = () => {
    onSubmit();
  };

  const handleInternalSubmit = () => {
    if (showDeleteInput) {
      formRef.current.handleSubmit();
    } else {
      onSubmit();
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} size="md" isCentered>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader py="15px">
          <KText color="secondary-900" size="md-medium">
            {title}
          </KText>
          {subTitle && (
            <KText
              color="secondary-700"
              size="sm-regular"
              mt="xs"
              whiteSpace="pre-line"
            >
              {subTitle}
            </KText>
          )}
        </ModalHeader>
        <ModalCloseButton bg="transparent" _hover={{}} />
        <ModalBody py="0">
          {customBody && customBody}
          {showDeleteInput && (
            <FormBuilder
              config={formConfig}
              formRef={formRef}
              initialValues={{}}
              onSubmit={formSubmit}
            />
          )}
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" mr="12px" onClick={onClose}>
            {cancelButtonLabel}
          </Button>
          <Button
            isLoading={submitButtonIsLoading}
            variant={primaryVariant}
            onClick={handleInternalSubmit}
            isDisabled={false}
            {...primaryButtonProps}
          >
            {submitButtonLabel}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
